import {Box, Divider, Stack, Typography} from '@mui/material';
import React, {useEffect, useState, useMemo} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {useTranslation, Trans} from 'react-i18next';
import './TextComponent.scss';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import {TextFormatEnum} from '../../../../../schemas/productions/productionSchema';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {
  useGetTextComponent,
  useGetTemplateTextComponent,
} from '../../../../../hooks/productions/useProductions';
import {ModuleType, FormMode} from '../../CreateNewProductionUtils';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  AnsiFormat,
  GetTemplateDetailsRequestParam,
  GetTextComponentResponse,
  GetTemplateTextComponentResponse,
} from '../../../../../api/types/production.type';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {setIsFieldsChanged} from '../../../../../redux/slice/productionSlice';

interface TextComponentProps {
  ansiList: AnsiFormat[];
  productionId: number;
  isProductionSet: boolean;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
}

const DEFAULT_TEXT_COMPONENT_ANSI_TYPE_ID = '21027';

const ENCODING_MAP = Object.fromEntries(
  TextFormatEnum.options.map((value, index) => [index, value])
);

const ansiIdsEqual = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
) => String(a ?? '') === String(b ?? '');

const findAnsiDropdownItem = (
  items: {id: string | number; name: string}[],
  formValue: string | number | null | undefined
) =>
  formValue == null || formValue === ''
    ? null
    : (items.find((opt) => ansiIdsEqual(opt.id, formValue)) ?? null);

const TextComponent: React.FC<TextComponentProps> = ({
  ansiList,
  productionId,
  isProductionSet,
  mode = FormMode.CREATE,
  isTemplate = false,
  templateId,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {
    control,
    setValue,
    reset,
    getValues,
    formState: {isDirty},
  } = useFormContext();

  useEffect(() => {
    if (isDirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  }, [isDirty]);
  const textFormat = useWatch({control, name: 'textComponentData.textFormat'});
  const shouldExportLargeTextFiles = useWatch({
    control,
    name: 'textComponentData.shouldExportLargeTextFiles',
  });
  const produceLoadFile = useWatch({
    control,
    name: 'textComponentData.produceLoadFile',
  });
  const [ANSI, UnicodeUTF16] = TextFormatEnum.options;

  const [open, setOpen] = useState(false);
  const isViewMode = mode === FormMode.VIEW;
  const textViewModeDisabledA11yProps = isViewMode
    ? ({
        'aria-disabled': true,
        tabIndex: -1,
      } as const)
    : {};
  const getTextInfoIconA11yProps = (label: string) => ({
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': label,
  });

  // Get Redux state
  const {securityGroupId} = useSelector((state: RootState) => state.production);

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  // API call to get text component data
  const {
    data: textComponentDataFromProduction,
    isLoading: isTextComponentLoadingFromProduction,
    error: textComponentErrorFromProduction,
  } = useGetTextComponent(
    {
      SecurityGroupId: securityGroupId,
      moduletype: moduleType,
      productionId: productionId,
      exportProductionId: 0,
    },
    !!securityGroupId && productionId > 0 && !isTemplate
  );

  const {
    data: textComponentDataFromTemplate,
    isLoading: isTextComponentLoadingFromTemplate,
    error: textComponentErrorFromTemplate,
  } = useGetTemplateTextComponent(
    {
      templateId: templateId!,
      securityGroupId: securityGroupId,
    } as GetTemplateDetailsRequestParam,
    !!securityGroupId && !!templateId && isTemplate
  );

  // Map template response to production response structure
  const mapTemplateToProduction = (
    templateData: GetTemplateTextComponentResponse
  ): GetTextComponentResponse => {
    return {
      textComponentModelDetails: {
        eLevelType: templateData.eLevelType,
        eTextFormat: templateData.eTextFormat,
        produceLoadFile: templateData.produceLoadFile,
        shouldSkipOCR: templateData.shouldSkipOCR,
        selectedANSIType: templateData.selectedANSIType,
        shouldExportLargeTextFiles: templateData.shouldExportLargeTextFiles,
      },
    };
  };

  // Use the appropriate data based on isTemplate flag - memoize to prevent infinite loops
  const textComponentData: GetTextComponentResponse | undefined =
    useMemo(() => {
      if (isTemplate && textComponentDataFromTemplate) {
        return mapTemplateToProduction(textComponentDataFromTemplate);
      }
      return textComponentDataFromProduction;
    }, [
      isTemplate,
      textComponentDataFromTemplate,
      textComponentDataFromProduction,
    ]);

  const isTextComponentLoading = isTemplate
    ? isTextComponentLoadingFromTemplate
    : isTextComponentLoadingFromProduction;
  const textComponentError = isTemplate
    ? textComponentErrorFromTemplate
    : textComponentErrorFromProduction;

  // Convert ANSI list to dropdown format
  const ansiDropdownItems = ansiList.map((item) => ({
    id: item.ansiTypeID,
    name: item.ansiTypeLabel,
  }));

  // Update form state based on API response
  useEffect(() => {
    if (textComponentData?.textComponentModelDetails) {
      const modelDetails = textComponentData.textComponentModelDetails;
      const textFormat = ENCODING_MAP[modelDetails.eTextFormat];
      const selectedANSIType =
        modelDetails.selectedANSIType != null &&
        String(modelDetails.selectedANSIType).trim() !== ''
          ? String(modelDetails.selectedANSIType)
          : ansiDropdownItems[0]?.id != null
            ? String(ansiDropdownItems[0].id)
            : DEFAULT_TEXT_COMPONENT_ANSI_TYPE_ID;
      const shouldSkipOCR = modelDetails.shouldSkipOCR;
      const shouldExportLargeTextFiles =
        modelDetails.shouldExportLargeTextFiles;
      const produceLoadFile = modelDetails.produceLoadFile;

      const currentValues = getValues();
      reset({
        ...currentValues,
        textComponentData: {
          textFormat,
          eTextFormat: modelDetails.eTextFormat,
          isAnsi: modelDetails.eTextFormat === 0,
          selectedANSIType,
          shouldSkipOCR,
          shouldExportLargeTextFiles,
          produceLoadFile,
        },
      });
    }
  }, [textComponentData, reset]);

  useEffect(() => {
    if (textFormat === ANSI) {
      setValue('textComponentData.eTextFormat', 0, {shouldValidate: true});
      setValue('textComponentData.isAnsi', true, {shouldValidate: true});
    } else if (textFormat === UnicodeUTF16) {
      setValue('textComponentData.eTextFormat', 1, {shouldValidate: true});
      setValue('textComponentData.isAnsi', false, {shouldValidate: true});
    }
  }, [textFormat, ANSI, UnicodeUTF16, setValue]);

  // Show loading state
  if (isTextComponentLoading) {
    return (
      <Box className="arr-text-encoding" sx={{textAlign: 'left'}}>
        <Typography
          variant="body1"
          data-testid={`text-component-loading-text-component-data`}
        >
          Loading text component data...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (textComponentError) {
    return (
      <Box className="arr-text-encoding" sx={{textAlign: 'left'}}>
        <Typography
          variant="body1"
          color="error"
          data-testid={`text-component-error-loading-text-component-data`}
        >
          Error loading text component data: {textComponentError.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="arr-text-encoding" sx={{textAlign: 'left'}}>
      <Typography
        component="h3"
        variant="body1"
        className={`typo-text subhead4_size text-headings`}
        id="text-component-encoding-label"
        data-testid={`text-component-encoding-label`}
      >
        {t(
          'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingLabel'
        )}
      </Typography>
      <Box
        className="arr-text-encoding-format__wrapper"
        display="flex"
        alignItems="center"
      >
        <Stack
          direction="row"
          flexWrap={'wrap'}
          spacing={8}
          alignItems="center"
          role="radiogroup"
          aria-labelledby="text-component-encoding-label"
        >
          <Controller
            name="textComponentData.textFormat"
            control={control}
            render={({field}) => (
              <ArrRadio
                dataTestId={t(
                  'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.ansi'
                )}
                size="small"
                {...field}
                data={[
                  {
                    label: t(
                      'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.ansi'
                    ),
                    value: ANSI,
                  },
                ]}
                className="text-headings"
                aria-disabled={isViewMode}
                tabIndex={isViewMode ? -1 : undefined}
                aria-checked={textFormat === ANSI}
                disabled={isViewMode}
              />
            )}
          ></Controller>
          <Controller
            name="textComponentData.selectedANSIType"
            control={control}
            render={({field}) => (
              <ArrSingleSelectDropdown
                dropdownLabel="Select ANSI"
                dropdownItems={ansiDropdownItems}
                selectedValues={findAnsiDropdownItem(
                  ansiDropdownItems,
                  field.value
                )}
                onSelectionChange={(item) => {
                  if (isViewMode) return;
                  field.onChange(
                    item?.id != null && item.id !== '' ? String(item.id) : ''
                  );
                }}
                onBlur={field.onBlur}
                sizeVariant="medium"
                customWidth={15}
                aria-disabled={isViewMode || textFormat === UnicodeUTF16}
                tabIndex={
                  isViewMode || textFormat === UnicodeUTF16 ? -1 : undefined
                }
                disabled={isViewMode || textFormat === UnicodeUTF16}
                searchable
                showNoResultsText
                dataTestId="asd-select-ansi"
              />
            )}
          ></Controller>
          <Controller
            name="textComponentData.textFormat"
            control={control}
            render={({field}) => (
              <ArrRadio
                dataTestId={t(
                  'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.unicode'
                )}
                size="small"
                {...field}
                data={[
                  {
                    label: t(
                      'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.unicode'
                    ),
                    value: UnicodeUTF16,
                  },
                ]}
                className="text-headings"
                aria-disabled={isViewMode}
                tabIndex={
                  isViewMode
                    ? textFormat === UnicodeUTF16
                      ? 0
                      : -1
                    : undefined
                }
                aria-checked={textFormat === UnicodeUTF16}
                disabled={isViewMode}
              />
            )}
          ></Controller>
        </Stack>
      </Box>
      <Box
        className="arr-text-encoding-info-points"
        component="ul"
        aria-labelledby="info-points-heading"
      >
        {Object.entries(
          t(
            'productionsAndExports.createNewProduction.productionComponents.textComponent.infoPoints'
          )
        ).map(([key, point], index) => (
          <li
            className="typo-text body3_size text-body-subtle"
            key={key}
            tabIndex={-1}
            data-testid={`text-component-li-${index}`}
          >
            {point}
          </li>
        ))}
      </Box>
      <Divider></Divider>
      <Typography
        component="h3"
        variant="body1"
        className="arr-text-ocr__wrapper typo-text subhead4_size text-headings"
        display="flex"
        gap={1}
        data-testid={`text-component-label`}
      >
        {t(
          'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrSection.label'
        )}
        <ArrTooltipWrapper
          title={t(
            'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrSection.tooltip.title'
          )}
          closeTooltipOnEscPress
          trigger={
            <InfoOutlinedIcon
              className="info-icon"
              {...getTextInfoIconA11yProps('OCR Options info')}
            />
          }
        >
          <Trans
            i18nKey="productionsAndExports.createNewProduction.productionComponents.textComponent.ocrSection.tooltip.content"
            components={{
              br: <br data-testid={`text-component-br`} />,
              strong: <strong data-testid={`text-component-strong`} />,
              p: <p data-testid={`text-component-p`} />,
            }}
          />
        </ArrTooltipWrapper>
      </Typography>
      <Stack
        direction="row"
        flexWrap={'wrap'}
        spacing={8}
        marginTop={3}
        role="radiogroup"
        aria-label={t(
          'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrSection.label'
        )}
      >
        <Controller
          name="textComponentData.shouldSkipOCR"
          control={control}
          render={({field}) => (
            <ArrRadio
              dataTestId={t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.noOcr'
              )}
              size="small"
              name="shouldSkipOCRTrue"
              value={String(field.value)}
              onChange={(value) => {
                if (isViewMode) return;
                field.onChange(value === 'true');
              }}
              data={[
                {
                  label: t(
                    'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.noOcr'
                  ),
                  value: 'true',
                },
              ]}
              className="text-headings"
              aria-disabled={isViewMode}
              aria-checked={field.value === true}
              tabIndex={
                isViewMode ? (field.value === true ? 0 : -1) : undefined
              }
              disabled={isViewMode}
            />
          )}
        ></Controller>
        <Controller
          name="textComponentData.shouldSkipOCR"
          control={control}
          render={({field}) => (
            <ArrRadio
              dataTestId={t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocr'
              )}
              size="small"
              name="shouldSkipOCRFalse"
              value={String(field.value)}
              onChange={(value) => {
                if (isViewMode) return;
                if (value === 'false') {
                  setOpen(true);
                  field.onChange(value === 'false');
                } else {
                  field.onChange(value === 'true');
                }
              }}
              data={[
                {
                  label: t(
                    'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocr'
                  ),
                  value: 'false',
                },
              ]}
              className="text-headings"
              aria-disabled={isViewMode}
              aria-checked={field.value === false}
              tabIndex={
                isViewMode ? (field.value === false ? 0 : -1) : undefined
              }
              disabled={isViewMode}
            />
          )}
        ></Controller>
      </Stack>
      <Controller
        name="textComponentData.shouldExportLargeTextFiles"
        control={control}
        render={({field}) => (
          <Box
            className="arr-text-large-txt-files native-toggle"
            tabIndex={isViewMode && shouldExportLargeTextFiles ? 0 : -1}
            aria-disabled={isViewMode}
            aria-label={t(
              'productionsAndExports.createNewProduction.productionComponents.textComponent.exportLargeTextFiles.label'
            )}
          >
            <ArrToggle
              role="switch"
              aria-checked={field.value}
              {...textViewModeDisabledA11yProps}
              {...field}
              disabled={isViewMode}
              checked={field.value}
              dataTestId="text-component-toggle"
            />

            <Typography
              component="h3"
              className="toggle-text typo-text subhead5_size"
              display="flex"
              alignItems="center"
              gap={1}
              data-testid={`text-component-label-text`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.exportLargeTextFiles.label'
              )}
              <ArrTooltipWrapper
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.textComponent.exportLargeTextFiles.tooltip.title'
                )}
                closeTooltipOnEscPress
                trigger={
                  <InfoOutlinedIcon
                    className="info-icon-sm"
                    {...getTextInfoIconA11yProps(
                      'Export Large Text Files info'
                    )}
                  />
                }
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.textComponent.exportLargeTextFiles.tooltip.content"
                  components={{
                    br: <br data-testid={`text-component-br-btn`} />,
                    strong: (
                      <strong data-testid={`text-component-strong-btn`} />
                    ),
                    p: <p data-testid={`text-component-p-text`} />,
                  }}
                />
              </ArrTooltipWrapper>
            </Typography>
          </Box>
        )}
      ></Controller>
      <Controller
        name="textComponentData.produceLoadFile"
        control={control}
        render={({field}) => (
          <Box
            className="arr-text-produce-load-file native-toggle"
            tabIndex={isViewMode && produceLoadFile ? 0 : -1}
            aria-disabled={isViewMode}
            aria-label={t(
              'productionsAndExports.createNewProduction.productionComponents.textComponent.generateTextLoadFile'
            )}
          >
            <ArrToggle
              role="switch"
              aria-checked={field.value}
              {...textViewModeDisabledA11yProps}
              {...field}
              disabled={isViewMode}
              checked={field.value}
              dataTestId="load-file-toggle"
            />

            <Typography
              className="toggle-text typo-text subhead5_size"
              data-testid={`text-component-generate-text-load-file`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.generateTextLoadFile'
              )}
            </Typography>
          </Box>
        )}
      ></Controller>
      <ArrModal
        showModal={open}
        onClose={() => setOpen(false)}
        headerTitle={t(
          'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.title'
        )}
        headerTitleClassName="headline3_size text-headings"
        className="text-component-modal"
        dataTestId="text-component-modal"
        buttons={[
          {
            id: '1',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.confirm'
            ),
            onClick: () => {
              setValue('textComponentData.shouldSkipOCR', false, {
                shouldValidate: true,
              });
              setOpen(false);
            },
            variant: 'primary',
            dataTestId: 'tcm-confirm-btn',
          },
          {
            id: '2',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.cancel'
            ),
            onClick: () => setOpen(false),
            variant: 'secondary',
            dataTestId: 'tcm-cancel-btn',
          },
        ]}
      >
        <Typography
          component="div"
          className="typo-text body2_size desc-text-color"
          color="white"
          aria-expanded={true}
          data-testid={`assignment-instructions`}
        >
          <Trans
            i18nKey="productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.description"
            components={{
              br: <br data-testid={`text-component-br-button`} />,
              strong: <strong data-testid={`text-component-strong-button`} />,
              p: <p data-testid={`text-component-p-btn`} />,
            }}
          />
        </Typography>
      </ArrModal>
    </Box>
  );
};

export default TextComponent;
